// „Таблица на резервациите" — чиста логика без React (тестове:
// scheduleLayout.test.ts): часовият прозорец, лентите по маси, броячите по
// часове и кои половинки час са затворени. Таблицата е за СМЯНА
// (`workingSlots.ts`): при работа след полунощ прозорецът минава през 24
// (петък 12:00–06:00 = 12…30), а минутите след полунощ са ≥ 1440 спрямо деня. Таблицата показва САМО движението
// на резервациите — маса, заета от сметка без резервация, тук не се вижда
// (тя е в „Резервации", на схемата).

import type { Reservation } from "../../../api/reservations";
import { MINUTE_MS, addDays, minuteOfDay, momentAtMinute, parseDateKey, toDateKey } from "../logic/dayTime";
import { isSlotPast } from "../logic/newReservation";
import { reservationEndMs, reservationStartMs } from "../logic/tableStatus";
import { SLOT_MINUTES, isSlotInHours, previousNightRangeOf, scheduleWindowOf } from "../logic/workingSlots";

/** Краят на календарния ден — краят на разписанието, когато смяната не минава през полунощ. */
export const SCHEDULE_END_HOUR = 24;
const DAY_MINUTES = 24 * 60;
/** Клетката е половин час — толкова е и най-малкото, което се кликва. */
export const SCHEDULE_CELL_MINUTES = 30;
// Половинката има два 15-минутни слота като във формата (:00 и :15, :30 и :45).
const CELL_SLOT_OFFSETS = [0, 15];
const DAY_MS = 24 * 60 * MINUTE_MS;

/**
 * Тонът на лентата — като на схемата: предстояща = „Резервирана", настанена =
 * „Заета"; плюс неизпълнена и приключила. Отказаните не заемат маса и не се
 * показват.
 */
export type ScheduleTone = "upcoming" | "seated" | "expired" | "completed";

export interface ScheduleBar {
  key: string;
  tone: ScheduleTone;
  // Минути от началото на деня, вече изрязани в прозореца.
  startMinute: number;
  endMinute: number;
  // Истинските начало/край (за надписа „19:00–21:00").
  fromMs: number;
  toMs: number;
  reservation: Reservation;
  // Пътеката в реда (застъпващите се ленти — напр. сметка върху резервация —
  // са една под друга) и колко пътеки има редът.
  lane: number;
  lanes: number;
}

export interface ScheduleTable {
  id: number;
  name: string;
  seats: number;
}

const TONE_OF_STATUS: Partial<Record<Reservation["status"], ScheduleTone>> = {
  upcoming: "upcoming",
  seated: "seated",
  expired: "expired",
  completed: "completed",
};

/**
 * Минутата от деня `dateKey` на момента — по часовника на стената (като
 * колоните, `momentAtMinute` и надписите), НЕ по изминали милисекунди от
 * полунощ: в деня на смяната на часа второто се разминава с час. Момент от
 * друг ден е с ±1440 на ден (полунощта в края на деня е 1440).
 */
export const minuteOfDayIn = (dateKey: string, moment: number) => {
  const momentDay = parseDateKey(toDateKey(new Date(moment))).getTime();
  const dayOffset = Math.round((momentDay - parseDateKey(dateKey).getTime()) / DAY_MS);
  return dayOffset * 24 * 60 + minuteOfDay(moment);
};

// Началата на резервациите в деня (минути); отпреди/след деня не броят.
// Докъде стига смяната на деня в минути (поне до полунощ).
const shiftLimitOf = (dateKey: string) => Math.max(SCHEDULE_END_HOUR, scheduleWindowOf(dateKey).endHour) * 60;

// Началата на резервациите в смяната (минути); отпреди/след нея не броят.
const startsInShift = (dateKey: string, dayMinutes: number[]) =>
  dayMinutes.filter((minute) => minute >= 0 && minute < shiftLimitOf(dateKey));

/**
 * Първият видим час на деня: часът на отварянето по работното време
 * (`scheduleWindowOf`) — или по-рано, ако в деня има резервация преди него
 * (`dayMinutes` — началата им).
 */
export const scheduleStartHour = (dateKey: string, dayMinutes: number[] = []) =>
  Math.min(
    scheduleWindowOf(dateKey).startHour,
    ...startsInShift(dateKey, dayMinutes).map((minute) => Math.floor(minute / 60)),
  );

/**
 * Краят на разписанието (час, без него): часът на затварянето по работното
 * време — или по-късно, ако в деня има резервация, започнала след него
 * (стара резервация в друг час не изчезва от таблицата).
 */
export const scheduleEndHour = (dateKey: string, dayMinutes: number[] = []) =>
  Math.max(
    scheduleWindowOf(dateKey).endHour,
    ...startsInShift(dateKey, dayMinutes).map((minute) => Math.floor(minute / 60) + 1),
  );

// Активна е резервацията, която още държи масата — предстояща или настанена.
const ACTIVE_STATUSES: ReadonlySet<Reservation["status"]> = new Set(["upcoming", "seated"]);

/**
 * „Скрий до 1-ва резервация": часът на първата активна резервация в
 * разписанието от `dayStartHour` — колоните преди него се крият. Закръгля се
 * надолу до часа, както прозорецът на деня (15:30 → от 15:00); не зависи от
 * „сега" и важи за всеки ден. Неизпълнените, приключилите и отказаните не
 * броят. null — няма какво да се скрие: няма активна резервация или първата
 * е още в първия час на прозореца (или тече от предишен ден).
 */
export const firstReservationHour = (
  reservations: Reservation[],
  dateKey: string,
  dayStartHour: number,
): number | null => {
  const startMinutes = reservations
    .filter((reservation) => ACTIVE_STATUSES.has(reservation.status) && isInSchedule(reservation, dateKey, dayStartHour))
    .map((reservation) => minuteOfDayIn(dateKey, reservationStartMs(reservation)))
    .filter((minute) => minute < shiftLimitOf(dateKey));
  if (startMinutes.length === 0) return null;
  const hour = Math.floor(Math.min(...startMinutes) / 60);
  return hour > dayStartHour ? hour : null;
};

/**
 * Лентите на масата в прозореца [startHour, endHour): резервациите ѝ без
 * отказаните, от началото до края им. Изрязани по прозореца; изцяло извън
 * него отпадат.
 */
export const barsOfTable = ({
  table,
  reservations,
  dateKey,
  startHour,
  endHour = SCHEDULE_END_HOUR,
}: {
  table: ScheduleTable;
  reservations: Reservation[];
  dateKey: string;
  startHour: number;
  endHour?: number;
}): ScheduleBar[] => {
  const windowStart = startHour * 60;
  const windowEnd = endHour * 60;
  const bars: ScheduleBar[] = [];
  const push = (bar: Omit<ScheduleBar, "startMinute" | "endMinute" | "lane" | "lanes">) => {
    const startMinute = Math.max(windowStart, minuteOfDayIn(dateKey, bar.fromMs));
    const endMinute = Math.min(windowEnd, minuteOfDayIn(dateKey, bar.toMs));
    if (endMinute > startMinute) bars.push({ ...bar, startMinute, endMinute, lane: 0, lanes: 1 });
  };

  for (const reservation of reservations) {
    const tone = TONE_OF_STATUS[reservation.status];
    if (!tone || !reservation.tableIds.includes(table.id)) continue;
    push({
      key: `r${reservation.id}`,
      tone,
      fromMs: reservationStartMs(reservation),
      toMs: reservationEndMs(reservation),
      reservation,
    });
  }
  return withLanes(bars.sort((left, right) => left.startMinute - right.startMinute));
};

/** Застъпващите се ленти на реда — по пътеки (първата свободна отгоре). */
const withLanes = (bars: ScheduleBar[]): ScheduleBar[] => {
  const laneEnds: number[] = [];
  const placed = bars.map((bar) => {
    const free = laneEnds.findIndex((end) => end <= bar.startMinute);
    const lane = free === -1 ? laneEnds.length : free;
    laneEnds[lane] = bar.endMinute;
    return { ...bar, lane };
  });
  return placed.map((bar) => ({ ...bar, lanes: laneEnds.length }));
};

/**
 * Резервацията е в разписанието на деня от `startHour`: краят ѝ е след
 * началото на прозореца. Отпада настанена от предишен ден, чиито гости още
 * седят (сървърът я връща и за днес) — тя няма лента, затова не влиза и в
 * броячите.
 */
export const isInSchedule = (reservation: Reservation, dateKey: string, startHour: number) =>
  reservationEndMs(reservation) > momentAtMinute(dateKey, startHour * 60);

/** Броячът на часа: колко резервации (без отказаните) текат в него и колко гости са. */
export const hourLoad = (reservations: Reservation[], dateKey: string, hour: number) => {
  const from = momentAtMinute(dateKey, hour * 60);
  const to = momentAtMinute(dateKey, (hour + 1) * 60);
  const active = reservations.filter(
    (reservation) =>
      reservation.status !== "cancelled" &&
      reservationStartMs(reservation) < to &&
      reservationEndMs(reservation) > from,
  );
  return {
    count: active.length,
    guests: active.reduce((sum, reservation) => sum + reservation.guests, 0),
  };
};

/**
 * С кой час се отваря „Нова резервация" при клик в половинката (минута
 * `minute`): първият ѝ 15-минутен слот, който е в работното време на деня и
 * още не е минал по правилото на формата (`isSlotPast`: 19:00 в 19:10 е
 * минал → 19:15). null — половинката е затворена: и двата ѝ слота са минали
 * (днес, от :15 / :45 нататък), извън работното време или денят е почивен.
 */
export const cellStartSlot = (dateKey: string, minute: number, nowMs: number): number | null => {
  for (const offset of CELL_SLOT_OFFSETS) {
    const slot = minute + offset;
    // След полунощ (≥ 1440) слотът е на следващата дата; връща се в минутите на деня.
    const slotDateKey = slot >= DAY_MINUTES ? addDays(dateKey, 1) : dateKey;
    const slotMinute = slot % DAY_MINUTES;
    if (isSlotInHours(slotDateKey, slotMinute) && !isSlotPast(slotDateKey, slotMinute, nowMs)) return slot;
  }
  return null;
};

/**
 * Резервациите на смяната `dateKey`: тези от деня без започналите след
 * полунощ в смяната на ПРЕДИШНИЯ ден (те са в неговата таблица) + тези от
 * следващия ден, започнали след полунощ в ТАЗИ смяна. `GET /reservations?date=`
 * връща по календарна дата на началото — нощта на смяната е в отговора за
 * следващия ден. Двойките (настанена от по-ранен ден идва и в днешния
 * отговор) се броят веднъж.
 */
export const reservationsOfShift = (
  dateKey: string,
  dayReservations: Reservation[],
  nextDayReservations: Reservation[],
): Reservation[] => {
  const nextDateKey = addDays(dateKey, 1);
  // Затварянето на нощта от предишния ден на датата (минута); 0 — няма нощ.
  const nightEndOf = (key: string) => {
    const night = previousNightRangeOf(key);
    return night ? night.lastSlot + SLOT_MINUTES : 0;
  };
  const ownNightEnd = nightEndOf(dateKey);
  const nextNightEnd = nightEndOf(nextDateKey);
  const startMinuteIn = (key: string, reservation: Reservation) => minuteOfDayIn(key, reservationStartMs(reservation));
  const byId = new Map<number, Reservation>();
  for (const reservation of dayReservations) {
    const minute = startMinuteIn(dateKey, reservation);
    if (!(minute >= 0 && minute < ownNightEnd)) byId.set(reservation.id, reservation);
  }
  for (const reservation of nextDayReservations) {
    const minute = startMinuteIn(nextDateKey, reservation);
    if (minute >= 0 && minute < nextNightEnd) byId.set(reservation.id, reservation);
  }
  return [...byId.values()];
};

/** В половинката не може да започне нова резервация — виж `cellStartSlot`. */
export const isCellClosed = (dateKey: string, minute: number, nowMs: number) =>
  cellStartSlot(dateKey, minute, nowMs) === null;
